<template>
  <div class="user-manual-container">
    <!-- Left Sidebar: Table of Contents -->
    <v-navigation-drawer
      v-model="leftDrawer"
      :permanent="$vuetify.breakpoint.mdAndUp"
      :temporary="$vuetify.breakpoint.smAndDown"
      absolute
      class="manual-sidebar"
      width="300"
    >
      <div class="sidebar-header">
        <div class="logo-container">
          <img :src="logo" alt="AI Kasa" class="header-logo" />
        </div>
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Pretraži..."
          outlined
          dense
          hide-details
          class="search-field mt-4"
        ></v-text-field>
      </div>

      <v-list class="nav-list" dense>
        <template v-for="section in filteredSections">
          <v-list-group
            :key="section.id"
            v-if="section.children"
            :value="isActiveSectionParent(section)"
            :prepend-icon="section.icon"
            no-action
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="nav-item-title">
                  {{ section.title }}
                </v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item
              v-for="child in section.children"
              :key="child.id"
              @click="selectSection(child)"
              :class="{ 'active-item': activeSection === child.id }"
              class="child-item"
            >
              <v-list-item-icon v-if="child.icon">
                <v-icon small>{{ child.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="child-title">
                  {{ child.title }}
                  <v-chip
                    v-if="child.badge"
                    x-small
                    :color="child.badge.color"
                    class="ml-1"
                  >
                    {{ child.badge.text }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>

          <v-list-item
            v-else
            :key="section.id"
            @click="selectSection(section)"
            :class="{ 'active-item': activeSection === section.id }"
          >
            <v-list-item-icon>
              <v-icon>{{ section.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="nav-item-title">
                {{ section.title }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content Area -->
    <div class="manual-main">
      <v-container fluid class="content-container">
        <!-- Breadcrumbs -->
        <div class="breadcrumbs-wrapper">
          <v-breadcrumbs :items="breadcrumbs" class="breadcrumbs-nav">
            <template v-slot:item="{ item }">
              <v-breadcrumbs-item
                :disabled="item.disabled"
                @click="item.click ? item.click() : null"
              >
                {{ item.text }}
              </v-breadcrumbs-item>
            </template>
          </v-breadcrumbs>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                @click="copyCurrentUrl"
                class="copy-url-btn"
              >
                <v-icon small>mdi-link-variant</v-icon>
              </v-btn>
            </template>
            <span>Kopiraj link na ovu sekciju</span>
          </v-tooltip>
        </div>

        <!-- Search Results -->
        <div v-if="showSearchResults" class="search-results-container">
          <div class="search-results-header">
            <h2 class="gradient-text">Rezultati pretrage</h2>
            <p class="search-info">
              Pronađeno {{ searchResults.length }} rezultat(a) za "{{ searchQuery }}"
            </p>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- No results -->
          <div v-if="searchResults.length === 0" class="no-results">
            <v-icon large color="grey">mdi-magnify-close</v-icon>
            <p class="mt-4">Nema rezultata za "{{ searchQuery }}"</p>
            <p class="text--secondary">Pokušajte s drugim pojmovima za pretragu</p>
          </div>

          <!-- Search result items -->
          <div v-else class="search-results-list">
            <div
              v-for="(result, index) in searchResults"
              :key="index"
              class="search-result-item glass-card"
              @click="selectSectionFromSearch(result.section)"
            >
              <div class="result-header">
                <v-icon small class="mr-2" color="primary">{{ result.section.icon }}</v-icon>
                <h3 class="result-title">{{ result.section.title }}</h3>
              </div>
              <div class="result-breadcrumb text--secondary">
                {{ result.breadcrumb }}
              </div>
              <div class="result-snippet" v-html="result.snippet"></div>
            </div>
          </div>
        </div>

        <!-- Content Card -->
        <div v-else class="content-card glass-card">
          <div class="content-header">
            <h1 class="gradient-text">{{ currentSectionTitle }}</h1>
            <v-chip
              v-if="currentSectionBadge"
              small
              :color="currentSectionBadge.color"
              class="ml-3"
            >
              {{ currentSectionBadge.text }}
            </v-chip>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Dynamic Content -->
          <div class="content-body" v-html="currentContent"></div>
        </div>

        <!-- Navigation Buttons -->
        <div class="navigation-buttons mt-6">
          <v-btn
            v-if="previousSection"
            @click="selectSection(previousSection)"
            class="soft-button"
            text
          >
            <v-icon left>mdi-chevron-left</v-icon>
            Prethodna sekcija
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="nextSection"
            @click="selectSection(nextSection)"
            class="soft-button"
            text
          >
            Sljedeća sekcija
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-container>
    </div>

    <!-- Right Sidebar: Quick Links -->
    <v-navigation-drawer
      v-if="$vuetify.breakpoint.lgAndUp"
      permanent
      right
      absolute
      class="manual-quick-links"
      width="250"
    >
      <div class="quick-links-header">
        <h4>Na ovoj stranici</h4>
      </div>
      <v-list dense>
        <v-list-item
          v-for="link in quickLinks"
          :key="link.id"
          @click="scrollToAnchor(link.anchor)"
          class="quick-link-item"
        >
          <v-list-item-content>
            <v-list-item-title class="quick-link-title">
              {{ link.title }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider class="my-4"></v-divider>

      <div class="quick-links-header">
        <h4>Vidi također</h4>
      </div>
      <v-list dense>
        <v-list-item
          v-for="related in relatedLinks"
          :key="related.id"
          @click="selectSection(related)"
          class="quick-link-item"
        >
          <v-list-item-icon>
            <v-icon small>{{ related.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="quick-link-title">
              {{ related.title }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider class="my-4"></v-divider>

      <div class="quick-links-header">
        <h4>Pomoć</h4>
      </div>
      <v-list dense>
        <v-list-item class="quick-link-item">
          <v-list-item-icon>
            <v-icon small>mdi-message-question</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="quick-link-title">
              Česta pitanja
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item class="quick-link-item">
          <v-list-item-icon>
            <v-icon small>mdi-email</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="quick-link-title">
              Kontakt podrška
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Mobile FAB for menu -->
    <v-btn
      v-if="$vuetify.breakpoint.smAndDown"
      @click="leftDrawer = !leftDrawer"
      fab
      fixed
      bottom
      left
      color="primary"
      class="mobile-menu-fab"
    >
      <v-icon>mdi-menu</v-icon>
    </v-btn>

    <!-- Snackbar for copy feedback -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      bottom
      right
      color="success"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Zatvori
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import categories from './categories'

export default {
  name: 'UserManual',
  data () {
    return {
      leftDrawer: null,
      searchQuery: '',
      activeSection: 'getting-started',
      snackbar: false,
      snackbarText: '',
      searchResults: [],
      sections: categories
    }
  },
  computed: {
    logo () {
      return require('@/assets/logo-AI-kasa.svg')
    },
    showSearchResults () {
      return this.searchQuery && this.searchQuery.trim().length >= 3
    },
    filteredSections () {
      if (!this.searchQuery) {
        return this.sections
      }
      const query = this.searchQuery.toLowerCase()
      return this.sections
        .map(section => {
          if (section.children) {
            const filteredChildren = section.children.filter(child =>
              child.title.toLowerCase().includes(query)
            )
            if (filteredChildren.length > 0 || section.title.toLowerCase().includes(query)) {
              return { ...section, children: filteredChildren }
            }
            return null
          } else {
            return section.title.toLowerCase().includes(query) ? section : null
          }
        })
        .filter(section => section !== null)
    },
    currentSectionTitle () {
      const section = this.findSectionById(this.activeSection)
      return section ? section.title : 'Dobrodošli u AI Kasa Priručnik'
    },
    currentSectionBadge () {
      const section = this.findSectionById(this.activeSection)
      return section && section.badge ? section.badge : null
    },
    currentContent () {
      // Dinamički content based na activeSection
      return this.getContentForSection(this.activeSection)
    },
    breadcrumbs () {
      const crumbs = [
        {
          text: 'Priručnik',
          disabled: false,
          click: () => this.selectSection(this.sections[0])
        }
      ]

      const section = this.findSectionById(this.activeSection)
      if (section) {
        const parent = this.findParentSection(this.activeSection)
        if (parent) {
          crumbs.push({
            text: parent.title,
            disabled: true
          })
        }
        crumbs.push({
          text: section.title,
          disabled: true
        })
      }

      return crumbs
    },
    quickLinks () {
      // Quick links za trenutnu sekciju
      return [
        { id: 1, title: 'Što je to?', anchor: 'what-is' },
        { id: 2, title: 'Kada koristiti?', anchor: 'when-to-use' },
        { id: 3, title: 'Korak po korak', anchor: 'step-by-step' }
      ]
    },
    relatedLinks () {
      // Related links based on current section
      return []
    },
    previousSection () {
      const allSections = this.getAllSectionsFlat()
      const currentIndex = allSections.findIndex(s => s.id === this.activeSection)
      return currentIndex > 0 ? allSections[currentIndex - 1] : null
    },
    nextSection () {
      const allSections = this.getAllSectionsFlat()
      const currentIndex = allSections.findIndex(s => s.id === this.activeSection)
      return currentIndex < allSections.length - 1 ? allSections[currentIndex + 1] : null
    }
  },
  methods: {
    selectSection (section) {
      this.activeSection = section.id
      if (this.$vuetify.breakpoint.smAndDown) {
        this.leftDrawer = false
      }
      // Update URL hash
      if (this.$route.hash !== `#${section.id}`) {
        this.$router.replace({ hash: `#${section.id}` }).catch(() => {})
      }
      // Scroll menu to show active section
      this.$nextTick(() => {
        this.scrollMenuToActive()
      })
      this.$vuetify.goTo(0)
    },
    selectSectionFromSearch (section) {
      // Clear search query to hide search results
      this.searchQuery = ''
      this.searchResults = []
      // Then navigate to the section
      this.selectSection(section)
    },
    copyCurrentUrl () {
      const url = `${window.location.origin}/user-manual#${this.activeSection}`
      navigator.clipboard.writeText(url).then(() => {
        this.snackbarText = 'Link kopiran u međuspremnik!'
        this.snackbar = true
      }).catch(() => {
        this.snackbarText = 'Greška pri kopiranju linka'
        this.snackbar = true
      })
    },
    loadSectionFromHash () {
      const hash = this.$route.hash
      if (hash && hash.startsWith('#')) {
        const sectionId = hash.substring(1)
        // Find section by ID
        let foundSection = null
        for (const section of this.sections) {
          if (section.id === sectionId) {
            foundSection = section
            break
          }
          if (section.children) {
            const child = section.children.find(c => c.id === sectionId)
            if (child) {
              foundSection = child
              break
            }
          }
        }
        if (foundSection) {
          this.activeSection = foundSection.id
          // Scroll menu to active section after DOM update
          this.$nextTick(() => {
            this.scrollMenuToActive()
          })
        }
      }
    },
    scrollMenuToActive () {
      // Wait longer for parent group animations to complete
      setTimeout(() => {
        const activeElement = document.querySelector('.nav-list .active-item')
        const sidebar = document.querySelector('.manual-sidebar .v-navigation-drawer__content')

        if (activeElement && sidebar) {
          // Get the actual position relative to the scrollable container
          let elementTop = 0
          let currentElement = activeElement

          // Calculate cumulative offsetTop from all parents until we reach the sidebar content
          while (currentElement && !currentElement.classList.contains('v-navigation-drawer__content')) {
            elementTop += currentElement.offsetTop || 0
            currentElement = currentElement.offsetParent
          }

          // Get dimensions
          const sidebarRect = sidebar.getBoundingClientRect()
          const elementRect = activeElement.getBoundingClientRect()
          const sidebarHeight = sidebarRect.height
          const elementHeight = elementRect.height

          // Center the element in the sidebar
          const targetScroll = elementTop - (sidebarHeight / 2) + (elementHeight / 2)

          // Smooth scroll to position
          sidebar.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          })
        }
      }, 800)
    },
    isActiveSectionParent (section) {
      if (!section.children) return false
      return section.children.some(child => child.id === this.activeSection)
    },
    findSectionById (id) {
      for (const section of this.sections) {
        if (section.id === id) return section
        if (section.children) {
          const child = section.children.find(c => c.id === id)
          if (child) return child
        }
      }
      return null
    },
    findParentSection (childId) {
      for (const section of this.sections) {
        if (section.children && section.children.some(c => c.id === childId)) {
          return section
        }
      }
      return null
    },
    getAllSectionsFlat () {
      const flat = []
      this.sections.forEach(section => {
        if (section.children) {
          section.children.forEach(child => flat.push(child))
        } else {
          flat.push(section)
        }
      })
      return flat
    },
    scrollToAnchor (anchor) {
      const element = document.getElementById(anchor)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    },
    getContentForSection (sectionId) {
      // Pronađi sekciju u categories
      for (const category of this.sections) {
        // Ako je to parent kategorija
        if (category.id === sectionId) {
          // Vrati content od parent kategorije ako postoji
          return category.content ? category.content() : this.getDefaultContent()
        }
        // Ako ima children, traži u children
        if (category.children) {
          const child = category.children.find(c => c.id === sectionId)
          if (child && child.content) {
            return child.content()
          }
        }
      }
      return this.getDefaultContent()
    },

    getDefaultContent () {
      return `
        <h2>Sadržaj u izradi</h2>
        <p>Ova sekcija priručnika je trenutno u izradi. Uskoro će biti dostupna.</p>
        <p>Za pomoć, molimo kontaktirajte našu podršku.</p>
      `
    },

    performFullTextSearch () {
      const query = this.searchQuery.trim()
      if (!query || query.length < 3) {
        this.searchResults = []
        return
      }

      const results = []
      const queryLower = query.toLowerCase()
      const allSections = this.getAllSectionsFlat()

      // Search through all sections
      allSections.forEach(section => {
        const content = this.getContentForSection(section.id)
        if (!content) return

        // Strip HTML tags to get plain text
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = content
        const plainText = tempDiv.textContent || tempDiv.innerText || ''

        // Check if query exists in the content
        const lowerText = plainText.toLowerCase()
        const index = lowerText.indexOf(queryLower)

        if (index !== -1) {
          // Extract snippet around the match
          const snippetLength = 200
          const start = Math.max(0, index - 50)
          const end = Math.min(plainText.length, index + query.length + snippetLength)
          let snippet = plainText.substring(start, end).trim()

          // Add ellipsis
          if (start > 0) snippet = '...' + snippet
          if (end < plainText.length) snippet = snippet + '...'

          // Highlight the matched term in the snippet
          const regex = new RegExp(`(${query})`, 'gi')
          const highlightedSnippet = snippet.replace(regex, '<mark class="search-highlight">$1</mark>')

          // Get breadcrumb path
          const parent = this.findParentSection(section.id)
          const breadcrumb = parent ? `${parent.title} > ${section.title}` : section.title

          results.push({
            section,
            snippet: highlightedSnippet,
            breadcrumb
          })
        }
      })

      this.searchResults = results
    }
  },
  mounted () {
    // Load section from URL hash if present
    this.loadSectionFromHash()
    // If no hash, set default section
    if (!this.$route.hash) {
      this.activeSection = 'getting-started'
    }
  },
  watch: {
    '$route.hash' (newHash) {
      this.loadSectionFromHash()
    },
    searchQuery (newQuery) {
      // Trigger full-text search when user types
      if (newQuery && newQuery.trim().length >= 3) {
        this.performFullTextSearch()
      } else {
        this.searchResults = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
.user-manual-container {
  position: relative;
  height: 100vh;
  display: flex;
  overflow: hidden;
}

// Left Sidebar
.manual-sidebar {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-right: 1px solid rgba(0, 179, 190, 0.2) !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  height: 100vh !important;
  overflow-y: auto !important;
  z-index: 4 !important;

  .sidebar-header {
    padding: 24px 16px;
    border-bottom: 1px solid rgba(0, 179, 190, 0.1);
    position: sticky;
    top: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    z-index: 10;

    .logo-container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0;

      .header-logo {
        height: 40px;
        width: auto;
      }
    }
  }

  .search-field {
    ::v-deep .v-input__control {
      border-radius: 12px !important;
    }
  }

  .nav-list {
    padding: 8px;

    .nav-item-title {
      font-weight: 500;
      font-size: 14px;
    }

    .child-title {
      font-size: 13px;
    }

    .active-item {
      background: linear-gradient(135deg,
        rgba(0, 179, 190, 0.1) 0%,
        rgba(57, 137, 201, 0.08) 50%,
        rgba(97, 84, 164, 0.1) 100%) !important;
      border-radius: 8px;

      .v-list-item__title {
        color: #3989C9 !important;
        font-weight: 600;
      }
    }
  }

  // Main category icons (parent groups) - solid blue - outside nav-list for higher specificity
  ::v-deep .v-list-group__header .v-icon {
    color: #3989C9 !important;
  }

  // Standalone items (without children) - solid blue
  ::v-deep .v-list-item:not(.child-item) .v-icon {
    color: #3989C9 !important;
  }

  // Child item icons - muted blue with reduced spacing
  ::v-deep .child-item {
    padding-left: 16px;

    .v-list-item__icon {
      margin-right: 12px !important;
    }

    .v-icon {
      color: rgba(57, 137, 201, 0.7) !important;
    }
  }
}

// Main Content
.manual-main {
  flex: 1;
  margin-left: 300px;
  margin-right: 0;
  background: linear-gradient(135deg,
    rgba(240, 248, 255, 0.3) 0%,
    rgba(255, 255, 255, 0.2) 100%);
  height: 100vh;
  overflow-y: auto;

  @media (max-width: 1263px) {
    margin-right: 0;
  }

  @media (max-width: 959px) {
    margin-left: 0;
  }
}

.content-container {
  max-width: 1200px;
  padding: 24px;
  margin: 0 auto;
}

.breadcrumbs-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.breadcrumbs-nav {
  background: transparent;
  padding: 8px 0;
  flex: 1;

  ::v-deep .v-breadcrumbs__item {
    font-size: 13px;
  }
}

.copy-url-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:hover {
    box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
    border-color: rgba(57, 137, 201, 0.3) !important;
    transform: translateY(-1px);
  }
}

.content-card {
  padding: 32px;
  min-height: 600px;
}

.content-header {
  display: flex;
  align-items: center;

  h1 {
    font-size: 32px;
    font-weight: 700;
    margin: 0;
  }
}

.content-body {
  font-size: 15px;
  line-height: 1.7;
  color: #333;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
}

// Right Sidebar
.manual-quick-links {
  display: none !important;

  .quick-links-header {
    padding: 16px;

    h4 {
      font-size: 14px;
      font-weight: 600;
      color: #3989C9;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .quick-link-item {
    border-radius: 8px;
    margin: 4px 8px;
    transition: all 0.2s;

    &:hover {
      background: rgba(0, 179, 190, 0.08);
    }

    .quick-link-title {
      font-size: 13px;
    }
  }
}

// Mobile FAB
.mobile-menu-fab {
  z-index: 1000;
}

// Gradient Text Utility
.gradient-text {
  font-weight: 600;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 1) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  display: inline-block !important;
}

// Glass Card Utility
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
}

// Soft Button Utility
.soft-button {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:hover {
    box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
    border-color: rgba(57, 137, 201, 0.3) !important;
    transform: translateY(-1px);
  }
}

// Responsive
@media (max-width: 960px) {
  .content-container {
    padding: 16px;
  }

  .content-card {
    padding: 20px;
  }

  .content-header h1 {
    font-size: 24px;
  }
}
</style>

<!-- Non-scoped styles for dynamically rendered content -->
<style lang="scss">
// Hide FAB on user manual page
.v-speed-dial {
  display: none !important;
}

.user-manual-container .content-body {
  h2 {
    font-size: 24px;
    font-weight: 600;
    margin-top: 32px;
    margin-bottom: 16px;
    background: linear-gradient(135deg,
      rgba(0, 179, 190, 1) 0%,
      rgba(57, 137, 201, 1) 50%,
      rgba(97, 84, 164, 1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    margin-top: 24px;
    margin-bottom: 12px;
    color: #3989C9;
  }

  h4 {
    font-size: 16px;
    font-weight: 600;
    margin-top: 16px;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 16px;
  }

  ul, ol {
    margin-bottom: 16px;
    padding-left: 24px;

    li {
      margin-bottom: 8px;
    }
  }

  code {
    background: rgba(0, 179, 190, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
  }

  .info-box,
  .warning-box,
  .success-box,
  .error-box {
    padding: 16px;
    border-radius: 12px;
    margin: 16px 0;
    display: flex;
    align-items: flex-start;
  }

  .info-box {
    background: rgba(57, 137, 201, 0.1);
    border-left: 4px solid rgba(57, 137, 201, 1);
  }

  .warning-box {
    background: rgba(255, 152, 0, 0.1);
    border-left: 4px solid rgba(255, 152, 0, 1);
  }

  .error-box {
    background: rgba(244, 67, 54, 0.1);
    border-left: 4px solid rgba(244, 67, 54, 1);
  }

  .success-box {
    background: rgba(76, 175, 80, 0.1);
    border-left: 4px solid rgba(76, 175, 80, 1);
  }

  .info-table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;

    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid rgba(0, 179, 190, 0.1);
    }

    th {
      font-weight: 600;
      background: rgba(0, 179, 190, 0.05);
    }
  }

  .status-chip {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &.success {
      background-color: rgba(76, 175, 80, 0.15);
      color: #4CAF50;
    }

    &.warning {
      background-color: rgba(255, 152, 0, 0.15);
      color: #FF9800;
    }

    &.error {
      background-color: rgba(244, 67, 54, 0.15);
      color: #F44336;
    }

    &.grey {
      background-color: rgba(158, 158, 158, 0.15);
      color: #9E9E9E;
    }
  }

  details {
    margin: 16px 0;
    padding: 16px;
    background: rgba(0, 179, 190, 0.05);
    border-radius: 8px;

    summary {
      font-weight: 600;
      cursor: pointer;
      user-select: none;
    }

    p {
      margin-top: 12px;
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin: 24px 0;
  }

  .feature-card {
    padding: 20px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(0, 179, 190, 0.15);
    text-align: center;

    h4 {
      margin: 12px 0 8px;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: #666;
    }
  }

  .glossary-section {
    dl {
      margin-left: 16px;
    }

    dt {
      font-weight: 600;
      margin-top: 16px;
      color: #3989C9;
    }

    dd {
      margin-left: 16px;
      margin-top: 8px;
      color: #555;
      line-height: 1.6;
    }
  }

  .faq-item {
    margin: 16px 0;
    padding: 16px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 12px;
    border: 1px solid rgba(0, 179, 190, 0.15);
    transition: all 0.3s;

    &:hover {
      background: rgba(255, 255, 255, 1);
      border-color: rgba(0, 179, 190, 0.3);
    }

    summary {
      font-weight: 600;
      cursor: pointer;
      user-select: none;
      color: #3989C9;
      font-size: 15px;

      &:hover {
        color: #00B3BE;
      }
    }

    p, ul, ol {
      margin-top: 12px;
    }
  }
}

// Search Results Styling
.search-results-container {
  padding: 20px;
}

.search-results-header {
  margin-bottom: 24px;

  h2 {
    font-size: 28px;
    margin-bottom: 8px;
  }

  .search-info {
    color: #666;
    font-size: 14px;
  }
}

.no-results {
  text-align: center;
  padding: 60px 20px;
  color: #999;

  p {
    margin-bottom: 8px;
  }
}

.search-results-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-result-item {
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 179, 190, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 179, 190, 0.15);
    border-color: rgba(0, 179, 190, 0.3);
  }

  .result-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    .result-title {
      font-size: 18px;
      font-weight: 600;
      color: #3989C9;
      margin: 0;
    }
  }

  .result-breadcrumb {
    font-size: 12px;
    margin-bottom: 12px;
    color: #999;
  }

  .result-snippet {
    font-size: 14px;
    line-height: 1.6;
    color: #555;
  }
}

::v-deep .search-highlight {
  background-color: #ffeb3b;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
  color: #000;
}

.breadcrumbs-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;

  .copy-url-btn {
    flex-shrink: 0;
  }
}
</style>
